import type { Attachment, ChatLogEntry } from '../types';
import { mergeToolCalls } from './logTransforms';

/**
 * A persisted trace record as replayed by `/get-thread-history` (and streamed
 * live as `{type: 'trace'}` events).
 */
export interface Trace {
  kind: 'prompt' | 'request' | 'response' | 'tool_call' | 'tool_response';
  ts: string;
  prompt?: string;
  response?: string;
  tool?: string;
  args?: string;
  agent?: string;
  requestId?: string;
  id?: string;
  durationMs?: number;
  toolCallId?: string;
  turn?: number;
  // For 'request' kind - contains user messages
  messages?: Array<{ role: number; text: string; attachments?: Attachment[] }>;
  attachments?: Attachment[];
  // Set by the agent on traces produced inside a subagent fork (Task
  // delegation). Absent on the agent the officer is talking to.
  forkKind?: 'sidechain' | 'thread';
  sidechainExecutionId?: string;
}

// Allowed trace kinds - same filtering for streaming as history loading
export const ALLOWED_TRACE_KINDS = ['prompt', 'request', 'response', 'tool_call', 'tool_response'];

// messages[].role on a request trace: 1 = user, 2 = assistant, 3 = tool result.
const ROLE_USER = 1;

function userMessageOf(trace: Trace) {
  return trace.messages?.find((m) => m.role === ROLE_USER);
}

/** Attachment metadata carried alongside the officer's message, if any. */
function userAttachmentsOf(trace: Trace): Attachment[] | undefined {
  const attachments = userMessageOf(trace)?.attachments ?? trace.attachments;
  return Array.isArray(attachments) && attachments.length > 0 ? attachments : undefined;
}

/**
 * Pull the officer-typed text out of a request trace's user message. The text
 * is a JSON-stringified content-block array, so only `text` blocks survive —
 * image/file blocks carry no prose to show.
 */
function userTextFromTrace(trace: Trace): string {
  const userMessage = userMessageOf(trace);
  if (!userMessage?.text) return '';
  try {
    const content = JSON.parse(userMessage.text);
    return content
      .filter((c: { type?: string; text?: string }) => c.type === 'text')
      .map((c: { text: string }) => c.text)
      .join('');
  } catch {
    return userMessage.text;
  }
}

/**
 * True when the trace came from a subagent fork rather than the agent the
 * officer is talking to.
 */
function isSidechainTrace(trace: Trace): boolean {
  return trace.forkKind === 'sidechain' || !!trace.sidechainExecutionId;
}

/**
 * Keep only the `request` traces that represent something the officer actually
 * typed.
 *
 * A `request` trace records just the LAST message handed to the model (the
 * agent's logger slices its message list to -1), which is why it doubles as the
 * user-message signal on replay. But several agent-loop mechanics put a
 * HumanMessage in that slot without the officer typing anything, and each one
 * used to render as a user bubble:
 *   - subagent forks — e.g. the observer's "Visual storyboard images loaded..."
 *     payload, or an evidence digest prompt;
 *   - context injected by middleware before the turn's first model call
 *     (evidence images, natively-attached PDFs);
 *   - mid-turn re-prompts (unsaved-narrative reminders, truncation retries).
 *
 * Only the turn-opening call on the officer's own agent can carry their
 * message, so that is the single request trace kept per turn — traces replay in
 * chronological order, so the first one seen for a turn is that call. Request
 * traces whose last message was a tool result carry no user text at all and are
 * dropped outright; they never rendered anything.
 */
export function withoutAgentLoopPrompts(traces: Trace[]): Trace[] {
  const turnsWithUserMessage = new Set<number>();

  return traces.filter((trace) => {
    if (trace.kind === 'prompt') {
      turnsWithUserMessage.add(trace.turn ?? 0);
      return true;
    }
    if (trace.kind !== 'request') return true;

    if (isSidechainTrace(trace)) return false;
    if (!userTextFromTrace(trace) && !userAttachmentsOf(trace)) return false;

    const turn = trace.turn ?? 0;
    if (turnsWithUserMessage.has(turn)) return false;
    turnsWithUserMessage.add(turn);
    return true;
  });
}

export function convertTraceToLogEntry(trace: Trace, idx: number): ChatLogEntry {
  const baseEntry = {
    id: trace.id || trace.requestId || `history-${idx}`,
    ts: trace.ts,
    status: 'complete' as const,
    requestId: trace.requestId,
    turn: trace.turn,
  };

  switch (trace.kind) {
    case 'prompt':
      return {
        ...baseEntry,
        kind: 'request',
        agent: 'User',
        prompt: trace.prompt || '',
      };
    case 'request':
      return {
        ...baseEntry,
        kind: 'request',
        agent: 'User',
        prompt: userTextFromTrace(trace),
        attachments: userAttachmentsOf(trace),
      };
    case 'response':
      return {
        ...baseEntry,
        kind: 'response',
        agent: trace.agent || 'Assistant',
        response: trace.response || '',
        durationMs: trace.durationMs,
      };
    case 'tool_call':
      return {
        ...baseEntry,
        kind: 'tool_call',
        tool: trace.tool,
        args: trace.args,
        toolCallId: trace.toolCallId,
        agent: trace.agent,
        durationMs: trace.durationMs,
      };
    case 'tool_response':
      return {
        ...baseEntry,
        kind: 'tool_response',
        toolCallId: trace.toolCallId,
        response: trace.response,
        agent: trace.agent,
        durationMs: trace.durationMs,
      };
    default:
      return {
        ...baseEntry,
        kind: 'response',
        response: '',
      };
  }
}

/**
 * Parse a get-thread-history NDJSON body into trace objects. Handles both the
 * wrapped `{ type: 'trace', data: {...} }` format and bare trace lines.
 */
export function parseThreadHistoryNdjson(text: string): Trace[] {
  const traces: Trace[] = [];
  for (const line of text.split('\n')) {
    if (!line.trim()) continue;
    try {
      const parsed = JSON.parse(line);
      if (parsed.type === 'trace' && parsed.data) {
        traces.push(parsed.data);
      } else if (parsed.kind) {
        traces.push(parsed);
      }
    } catch {
      // Skip malformed lines
    }
  }
  return traces;
}

/** Convert history traces into merged log entries flagged as historical. */
export function historicalEntriesFromTraces(traces: Trace[]): ChatLogEntry[] {
  const allowed = traces.filter((t) => ALLOWED_TRACE_KINDS.includes(t.kind));
  const rawEntries = withoutAgentLoopPrompts(allowed).map((t, idx) =>
    convertTraceToLogEntry(t, idx),
  );
  return mergeToolCalls(rawEntries).map((entry) => ({ ...entry, isHistorical: true }));
}
